/* Progressive enhancement; native Shopify product forms are preserved. */
(() => {
  'use strict';
  // Older layouts may also include this file during a section-only update.
  if (document.documentElement.hasAttribute('data-product-details-initialized')) return;
  document.documentElement.setAttribute('data-product-details-initialized', '');
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  const money = cents => new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
  const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  const productData = section => JSON.parse(section.querySelector('[data-product-json]')?.textContent || '{}');

  function syncProduct(section) {
    const id = section.querySelector('[data-product-form] [name="id"]')?.value;
    const data = productData(section);
    const variant = data.variants?.find(item => String(item.id) === id);
    if (!variant) return;
    const planId = section.querySelector('[data-selling-plan-select]')?.value;
    const allocation = variant.selling_plan_allocations?.find(item => String(item.selling_plan.id) === planId);
    const price = allocation ? allocation.price : variant.price;
    const compare = allocation ? allocation.compare_at_price : variant.compare_at_price;
    const quantity = Number(section.querySelector('[name="quantity"]')?.value || 1);
    const packs = [...section.querySelectorAll('[data-pack-quantity]')];
    const allowDisplayPrice = !!section.dataset.bundleDisplayCurrency && section.dataset.bundleDisplayCurrency === config.currency;
    const quote = (count, input) => window.HomiHavenBundlePricing?.quote({
      price, compare, quantity: count, breaks: variant.quantity_price_breaks || [],
      displayTotal: input?.dataset.packDisplayPrice || '', allowDisplayPrice, subscription: !!allocation
    }) || (Number.isSafeInteger(count) && count > 0 ? { total: price * count, compareTotal: (compare || 0) * count, usesDisplayPrice: false } : null);
    section.querySelectorAll('[data-product-option]').forEach(input => { input.checked = variant.options?.[Number(input.dataset.productOption)] === input.value; });
    section.querySelectorAll('[data-option-group]').forEach(group => { group.querySelector('[data-option-value]').textContent = variant.options?.[Number(group.dataset.optionGroup)] || ''; });
    let matchedPack = false;
    packs.forEach(input => {
      const count = Number(input.value), min = variant.quantity_rule?.min || 1, max = variant.quantity_rule?.max, increment = variant.quantity_rule?.increment || 1;
      input.disabled = !variant.available || count < min || (max != null && count > max) || (count - min) % increment !== 0;
      input.checked = count === quantity && !input.disabled && !matchedPack;
      if (input.checked) matchedPack = true;
      const bundlePrice = quote(count, input);
      input.closest('label').querySelector('[data-pack-price]').textContent = bundlePrice ? money(bundlePrice.total) : '—';
      const comparePrice = input.closest('label').querySelector('[data-pack-compare-price]');
      if (comparePrice) {
        const showCompare = bundlePrice && bundlePrice.compareTotal > bundlePrice.total;
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? money(bundlePrice.compareTotal) : '';
      }
    });
    const selectedPrice = quote(quantity, packs.find(input => input.checked));
    const total = section.querySelector('[data-product-total]');
    if (total) total.textContent = selectedPrice ? money(selectedPrice.total) : '—';
    const pricingNote = section.querySelector('[data-bundle-price-note]');
    if (pricingNote) pricingNote.hidden = !packs.some(input => quote(Number(input.value), input)?.usesDisplayPrice);
    const quantityLabel = section.querySelector('[data-product-total-quantity]');
    if (quantityLabel) quantityLabel.textContent = Number.isFinite(quantity) && quantity > 0 ? quantity : '0';
    const unit = section.querySelector('[data-product-total-unit]');
    if (unit) unit.textContent = quantity === 1 ? (section.dataset.unitSingular || 'item') : (section.dataset.unitPlural || 'items');
    const sale = section.querySelector('[data-product-sale]');
    if (sale) sale.hidden = !(compare > price);
  }

  function selectOption(input) {
    const section = input.closest('[data-product-section]');
    const variants = productData(section).variants || [];
    const position = Number(input.dataset.productOption);
    const values = [...section.querySelectorAll('[data-option-group]')].map(group => group.querySelector('input:checked')?.value);
    // Keep the other selections when possible, then choose an existing combination.
    const variant = variants.find(item => item.options?.every((value, index) => value === values[index]))
      || variants.find(item => item.available && item.options?.[position] === input.value)
      || variants.find(item => item.options?.[position] === input.value);
    if (!variant) { syncProduct(section); return; }
    const select = section.querySelector('[data-variant-select]');
    if (select) { select.value = String(variant.id); select.dispatchEvent(new Event('change', { bubbles: true })); }
    syncProduct(section);
  }

  function carouselState(section) {
    const track = section.querySelector('[data-carousel-track]');
    if (!track) return;
    const cards = [...track.children], rect = track.getBoundingClientRect();
    const visible = cards.map((card, index) => ({ index, rect: card.getBoundingClientRect() })).filter(card => card.rect.left < rect.right - 12 && card.rect.right > rect.left + 12);
    const count = section.querySelector('[data-carousel-count]');
    if (count) count.textContent = visible.length ? `${visible[0].index + 1}–${visible.at(-1).index + 1} / ${cards.length}` : '';
    const previous = section.querySelector('[data-carousel-direction="-1"]'), next = section.querySelector('[data-carousel-direction="1"]');
    if (previous) previous.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  function moveCarousel(section, direction) {
    const track = section.querySelector('[data-carousel-track]');
    if (!track?.firstElementChild) return;
    const step = track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
    track.scrollBy({ left: direction * step, behavior: behavior() });
  }

  let lightbox, lightboxTrigger;
  function openLightbox(link) {
    if (!lightbox) {
      lightbox = document.createElement('dialog');
      lightbox.className = 'product-lightbox'; lightbox.setAttribute('aria-label', 'Enlarged product image');
      const close = document.createElement('button');
      close.type = 'button'; close.className = 'product-circle-button product-lightbox__close';
      close.setAttribute('aria-label', 'Close image'); close.textContent = '✕';
      close.addEventListener('click', () => lightbox.close());
      const image = document.createElement('img'), caption = document.createElement('p');
      lightbox.append(close, image, caption); document.body.append(lightbox);
      lightbox.addEventListener('click', event => { if (event.target === lightbox) { const rect = lightbox.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) lightbox.close(); } });
      lightbox.addEventListener('close', () => lightboxTrigger?.focus({ preventScroll: true }));
    }
    lightboxTrigger = link;
    lightbox.querySelector('img').src = link.href;
    lightbox.querySelector('img').alt = link.querySelector('img')?.alt || link.dataset.lightboxCaption;
    lightbox.querySelector('p').textContent = link.dataset.lightboxCaption;
    lightbox.showModal();
  }

  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-product-option]')) selectOption(target);
    const section = target.closest('[data-product-section]');
    if (!section) return;
    if (target.matches('[data-pack-quantity]')) {
      const input = section.querySelector('[name="quantity"]'); input.value = target.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncProduct(section);
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-product-section] [name="quantity"]')) syncProduct(event.target.closest('[data-product-section]'));
  });
  document.addEventListener('click', event => {
    const direction = event.target.closest('[data-carousel-direction]');
    if (direction) moveCarousel(direction.closest('[data-product-carousel]'), Number(direction.dataset.carouselDirection));
    const image = event.target.closest('[data-product-lightbox]');
    if (image && typeof HTMLDialogElement !== 'undefined' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) { event.preventDefault(); openLightbox(image); }
  });
  document.addEventListener('keydown', event => {
    const track = event.target.closest('[data-carousel-track]');
    if (track && event.target === track && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); moveCarousel(track.closest('[data-product-carousel]'), event.key === 'ArrowRight' ? 1 : -1); }
  });

  // Keep native video controls usable, with only one story playing at a time.
  document.addEventListener('play', event => {
    if (!event.target.matches('.product-story__video')) return;
    document.querySelectorAll('.product-story__video').forEach(video => { if (video !== event.target) video.pause(); });
  }, true);
  const storyVideoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
  }, { threshold: 0 }) : null;

  function init(scope = document) {
    scope.querySelectorAll('.product-story__video').forEach(video => storyVideoObserver?.observe(video));
    scope.querySelectorAll('[data-product-section]').forEach(section => {
      const picker = section.querySelector('[data-option-picker]');
      if (picker && productData(section).variants?.every(variant => Array.isArray(variant.options))) { picker.hidden = false; section.classList.add('has-option-picker'); }
      section.querySelectorAll('[data-product-packs], [data-product-total-row]').forEach(element => { element.hidden = false; });
      syncProduct(section);
    });
    scope.querySelectorAll('[data-product-carousel]').forEach(section => {
      const track = section.querySelector('[data-carousel-track]');
      if (!track || section.dataset.carouselReady) return;
      section.dataset.carouselReady = 'true';
      section.querySelectorAll('[data-carousel-controls]').forEach(element => { element.hidden = false; });
      track.addEventListener('scroll', () => carouselState(section), { passive: true });
      if ('ResizeObserver' in window) { const observer = new ResizeObserver(() => carouselState(section)); observer.observe(track); section.carouselObserver = observer; }
      carouselState(section);
    });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('product:bundle-pricing-ready', () => init());
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.product-story__video').forEach(video => { video.pause(); storyVideoObserver?.unobserve(video); });
    event.target.querySelectorAll('[data-product-carousel]').forEach(section => section.carouselObserver?.disconnect());
    if (lightbox?.open) lightbox.close();
  });
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest('.product-story, .product-review');
    if (card) card.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
  });
  init();
})();
